// @ts-strict-ignore
/** @jsx jsx */
/* eslint-disable camelcase */
import LegacyProductTableRow, {
  CtaCell,
  DataCell,
  ImageCell,
  PercentageCell,
  MoreInformationButton,
} from '../legacy-product-table-row'
import {
  MoreInformationBlock,
  MoreInformationText,
  MoreInformationTable,
  MoreInformationList,
} from '@uswitch/ev-components/lib/components/compounds/information-panel'
import { timeout, ComponentTrackingContext } from '@uswitch/ev-utils/component-events'
import CallOut from '@uswitch/ev-components/lib/components/elements/call-out'
import { jsx } from 'theme-ui'
import {
  getValue,
  getArrayValue,
  getDisabledValue,
  getHrefValue,
  getFirstValue,
} from '@u/product-schema'
import Image from '../imgix-img'
import React from 'react'
import { trackProductClickout, trackTableLoad, gtmTrack } from '@u/tracking'
import { findAddon, filterAddons } from '@u/addons'
import guid from '@u/guid'
import { getLinkAndText, getLink } from '@u/get-link-from-array'
import { getCtaText, clickableRowValue } from '@u/product-table'
import { getMoreInformationContent, getValidHref } from './utils'

import { BannerModal } from './banner-modal'
import { ProductTableModal } from './product-table-modal'
import { PadWithDecimals } from '@h/number-formatter'

const formatTracking = (tracking, data, trackingKey) => {
  const formattedTracking = {}

  for (const key in tracking[trackingKey]) {
    if (Object.prototype.hasOwnProperty.call(tracking[trackingKey], key)) {
      formattedTracking[key] = getValue(tracking[trackingKey][key], data)
    }
  }

  if (tracking.listLoadedId) {
    // eslint-disable-next-line camelcase
    formattedTracking.list_loaded_id = tracking.listLoadedId
  }

  return formattedTracking
}

const getHref = (cell, data, clickableRow, productId, query) => {
  let hrefValue = getHrefValue(cell.primary.href, data)

  if (Array.isArray(cell.product || cell.primary)) {
    const { clickoutHrefFromSchema } = getLinkAndText(cell.primary, query, data)

    hrefValue = clickoutHrefFromSchema
  }
  return !clickableRow && hrefValue ? hrefValue : null
}

class ConnectedLegacyProductTable extends React.PureComponent {
  static trackComponent = ComponentTrackingContext

  constructor(props) {
    super(props)
    this.state = {
      listLoadedId: guid(),
      bannerModalOpen: false,
    }
  }

  tracking(eventName) {
    const { schemaTracking, tracking, data } = this.props
    const { listLoadedId } = this.state
    trackTableLoad({
      ...formatTracking({ ...tracking, listLoadedId }, {}, 'table'),
      products: this.props.data.map((d, i) => ({
        ...formatTracking({ ...tracking, listLoadedId }, d, 'segment'),
        position: i,
      })),
    })
    schemaTracking.tableEvent(eventName, { products: data })
  }

  componentDidUpdate() {
    this.tracking('table-updated')
  }

  componentDidMount() {
    this.tracking('table-loaded')
  }

  render() {
    const {
      data = [],
      grid = { contents: [] },
      addons,
      header,
      clickableRow,
      tracking,
      schemaTracking,
      settings = {},
      product_id,
      query,
      isExpired,
      eligibilityProviderTimeout,
      variant,
      brand,
      datasetId,
      schemaId,
      tableId,
      name,
    } = this.props

    const isWireFrame = this.props.isWireFrameInitial || this.props.isExpired

    const phoneAddon = findAddon('phoneNumber', addons)

    const onClickOut = (e, href, data, index) => {
      e.preventDefault()
      e.stopPropagation()
      timeout(
        trackComponent('Product Table Event', 'ctaClick', {
          brand,
          index,
          product_id: data?._source?.definition?.id,
          product_name: data?._source?.definition?.display_name,
        }),
      ).then(() => {
        window.location.href = href
      })
      trackProductClickout(formatTracking({ ...tracking, listLoadedId }, data, 'segment'))
      schemaTracking.productEvent('product-clicked', data, index)
      schemaTracking.productEvent('product-clickout', data, index)
    }

    const ctaClick = (e, href, data, index) => {
      if (!clickableRow) {
        onClickOut(e, href, data, index)
      }
    }

    const onSecondaryClick = (text, data) => {
      const productID = data?._id || data?._source?._id

      gtmTrack('GA Button Clicked', 'Functional Interaction', `${text}: ${productID}`)
    }

    const applyColors = (cells) => {
      const colors = [
        {
          background: '#fef6ed',
          border: '#fcdbb7',
          color: '#191919',
        },
        {
          background: '#fef1ec',
          border: '#fac9b0',
          color: '#191919',
        },
        {
          background: '#fbedf1',
          border: '#f0b9c8',
          color: '#191919',
        },
        {
          background: '#F6ECF0',
          border: '#EAD3DD',
          color: '#191919',
        },
        {
          background: '#F4ECF3',
          border: '#E3D0E1',
          color: '#191919',
        },
      ]

      const colorsTest = [
        {
          background: '#fff',
          border: '#fff',
          color: '#000000',
        },
        {
          background: '#fff',
          border: '#fff',
          color: '#000000',
        },
        {
          background: '#fbedf1',
          border: '#fbedf1',
          color: '#000000',
        },
        {
          background: '#fff',
          border: '#fff',
          color: '#000000',
        },
        {
          background: '#fff',
          border: '#fff',
          color: '#000000',
        },
      ]

      return cells.reduce(
        (acc, curr, i, sourceArr) => {
          let returnValue

          if (curr.type === 'cells.Content' || curr.type === 'cells.CircularPercentageBar') {
            if (acc.colors.length > 0) {
              returnValue = {
                cells: [...acc.cells, { ...curr, colors: acc.colors.slice(0, 1)[0] }],
                colors: acc.colors.slice(1),
              }
            } else {
              returnValue = {
                cells: [...acc.cells, { ...curr, colors: {} }],
                colors: [],
              }
            }
          } else {
            returnValue = { cells: [...acc.cells, curr], colors: acc.colors }
          }
          return i === sourceArr.length - 1 ? returnValue.cells : returnValue
        },
        {
          cells: [],
          colors: variant === 'reducedSpacingTwo' ? colorsTest : colors,
        },
      )
    }

    const representativeExample = (data) => {
      const addonFooter = findAddon('footer', addons)

      return addonFooter && getFirstValue(addonFooter.footer, data)
    }

    const info = (data) => {
      const infoArr = []
      filterAddons('info', addons).forEach((addon) =>
        addon.info.forEach((item) => infoArr.push(getValue(item, data))),
      )
      return infoArr
    }

    const ctaAddon = findAddon('moreInformationCTA', addons)
    const moreInformationButton = (product, index) => {
      if (!ctaAddon) return

      const { ctaTextFromSchema, clickoutHrefFromSchema } = getLinkAndText(
        ctaAddon.link,
        query,
        product,
      )

      return (
        ctaTextFromSchema &&
        clickoutHrefFromSchema && (
          <MoreInformationButton
            onClick={() => onClickOut(product, index)}
            href={clickoutHrefFromSchema}
          >
            {ctaTextFromSchema}
          </MoreInformationButton>
        )
      )
    }

    const moreInformationPanel = (product) => {
      const displayBlocks = filterAddons('moreInformationBlock', addons)
        .map((addon) => {
          const panel = {
            title: getValue(addon.title, product),
            view: addon.view,
          }
          panel.content = getMoreInformationContent(addon, product)

          return panel
        })
        .filter((panel) => {
          return (
            panel !== null &&
            panel.content &&
            panel.content?.length > 0 &&
            !panel.content.every((chunk) => chunk === null)
          )
        })

      return (
        <For each='addon' of={displayBlocks} index='key'>
          <div
            sx={{
              '@media screen and (max-width: 425px)':
                variant === 'legacyNewMobileStyling'
                  ? { '& > div > div': { padding: 'unset' } }
                  : {},
              display: 'contents',
            }}
          >
            <MoreInformationBlock key={key} title={addon.title}>
              <If condition={addon.view === 'list'}>
                <MoreInformationList rows={addon.content} />
              </If>
              <If condition={addon.view === 'table'}>
                <MoreInformationTable rows={addon.content} />
              </If>
              <If condition={addon.view === 'HTML'}>
                <MoreInformationText content={addon.content} />
              </If>
            </MoreInformationBlock>
          </div>
        </For>
      )
    }

    const badges = (product) => {
      if (header.badges) {
        return header.badges.map((badge) => getValue(badge, product))
      } else if (header.badge) {
        return [getValue(header.badge, product)]
      }
    }

    const phone = (product) => {
      if (!phoneAddon) {
        return null
      }

      const phoneAddonKeys = [
        'phoneNumber',
        'logoUrl',
        'logoDescription',
        'openingTimes',
        'termsAndConditions',
        'complianceText',
        'trackingUrl',
      ]
      const phoneValues = phoneAddonKeys.reduce(
        (obj, key) => ({ ...obj, [key]: getArrayValue(phoneAddon[key], product) }),
        {},
      )

      if (!phoneValues.phoneNumber) {
        return null
      }

      let url = null

      if (Array.isArray(phoneAddon.url)) {
        url = getLink(phoneAddon.url, query, product)
      }

      return { ...phoneValues, url }
    }

    const formatCell = (value, unit = {}) => {
      if (unit.value === 'full-pounds') {
        const formatted = new Intl.NumberFormat('en-GB', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(value)
        return `£${formatted}`
      }

      if (unit.value === 'exchange-rate') {
        return PadWithDecimals(4, value)
      }

      if (value === true) {
        return 'Yes'
      }

      if (value === false) {
        return 'No'
      }

      return value
    }

    const openModal = (index) => {
      const phoneData = phone(data[index])

      if (phoneData.trackingUrl) {
        fetch(`${phoneData.trackingUrl}?position=${index + 1}`)
          .then(() => {})
          .catch(() => {})
      } else if (window.Sentry) {
        window.Sentry.captureException(
          new Error('Missing phone tracking URL, needs to be added to schema'),
        )
      }

      setPhoneNumberModal(index)
    }

    const bannerInfo = () => {
      if (!header.banner || !header.banner.text || !header.banner.linkText) {
        return
      }

      const bannerValues = {
        text: getValue(header.banner.text).trim(),
        linkText: getValue(header.banner.linkText).trim(),
      }

      return bannerValues
    }

    return (
      <div
        data-fs-core-product-table
        data-dataset-id={datasetId}
        data-schema-id={schemaId}
        data-contentful-table-id={tableId}
        data-contentful-name={name}
      >
        {isExpired && <ProductTableModal eligibilityProviderTimeout={eligibilityProviderTimeout} />}

        <Choose>
          <When condition={data.length === 0}>
            <CallOut
              sx={{ marginTop: 'md' }}
              bold={true}
              text='There are no products matching your search.'
            />
          </When>
          <Otherwise>
            {this.state.bannerModalOpen && (
              <BannerModal
                onClose={() => {
                  this.setState({ bannerModalOpen: false })
                }}
              />
            )}

            <For each='product' of={data} index='index'>
              <LegacyProductTableRow
                brand={brand}
                data={product}
                representativeExample={representativeExample(product)}
                info={info(product)}
                title={getValue(header.title, product)}
                moreInformationButton={
                  getDisabledValue(settings.disablingField, product)
                    ? null
                    : moreInformationButton(product, index)
                }
                moreInformationPanel={moreInformationPanel(product)}
                moreInformationLabel={settings?.moreInformationLabel?.value}
                clickableRow={
                  !phone(product)?.openingTimes &&
                  clickableRowValue(clickableRow, query, product, index)
                }
                onLinkClick={
                  !phone(product)?.openingTimes && clickableRow
                    ? () => onClickOut(product, index)
                    : null
                }
                onRowClick={phone(product)?.openingTimes ? () => openModal(index) : null}
                disabled={getDisabledValue(settings.disablingField, product)}
                badges={badges(product)}
                preapproved={getValue(header.preapproved, product)}
                bannerInfo={bannerInfo(product)}
                onBannerClick={() => {
                  this.setState({ bannerModalOpen: true })
                }}
                telephone={phone(product)?.phoneNumber}
                key={index}
                index={index}
                isWireFrame={isWireFrame}
                variant={variant}
                product_id={product?._id || product?._source?._id}
              >
                <For each='cell' of={applyColors(grid.contents)} index='key'>
                  <If condition={cell.type === 'cells.Content'}>
                    <DataCell
                      label={getValue(cell.label.value, product)}
                      backgroundColor={cell.colors.background}
                      borderBottomColor={cell.colors.border}
                      color={cell.colors.color}
                      key={key}
                      isWireFrame={isWireFrame}
                      variant={variant}
                    >
                      {formatCell(getFirstValue(cell.content.text, product), cell.content.unit)}
                      <span>{getValue(cell.content.subscript, product)}</span>
                      <span>{getValue(cell.content.secondaryText, product)}</span>
                    </DataCell>
                  </If>

                  <If condition={cell.type === 'cells.Cta'}>
                    <CtaCell
                      href={getHref(cell, product, clickableRow, product_id, query)}
                      secondaryHref={getValidHref(cell.secondary, product, query).clickoutHref}
                      secondaryText={getValidHref(cell.secondary, product, query).ctaText}
                      onClick={(e) =>
                        ctaClick(
                          e,
                          getHref(cell, product, clickableRow, product_id, query),
                          product,
                          index,
                        )
                      }
                      onSecondaryClick={() =>
                        onSecondaryClick(
                          getValidHref(cell.secondary, product, query).ctaText,
                          product,
                        )
                      }
                      disabled={getDisabledValue(settings.disablingField, product)}
                      key={key}
                      isWireFrame={isWireFrame}
                      clickableRow={clickableRow}
                      variant={variant}
                      product_id={product?._id || product?._source?._id}
                      rowPosition={index}
                    >
                      {getCtaText(cell, query, product, phoneAddon)}
                    </CtaCell>
                  </If>

                  <If condition={cell.type === 'cells.Image'}>
                    <ImageCell key={key} isWireFrame={isWireFrame} variant={variant}>
                      {getValue(cell.image, product) && (
                        <Image
                          width={260}
                          src={getValue(cell.image, product)}
                          sx={{ maxWidth: '100%' }}
                          alt={getValue(cell.alt, product)}
                        />
                      )}
                    </ImageCell>
                  </If>

                  <If condition={cell.type === 'cells.CircularPercentageBar'}>
                    <PercentageCell
                      label={getValue(cell.label.value, product)}
                      backgroundColor={cell.colors.background}
                      borderBottomColor={cell.colors.border}
                      color={cell.colors.color}
                      key={key}
                      percentage={parseInt(getValue(cell.content.text, product), 10)}
                      alternativeText={'No results found for this'}
                      isWireFrame={isWireFrame}
                    />
                  </If>
                </For>
              </LegacyProductTableRow>
            </For>
          </Otherwise>
        </Choose>
      </div>
    )
  }
}

export default ConnectedLegacyProductTable
